import React, { useState } from "react";
import { ArrowLeftIcon, FileTextIcon, TrashIcon, UploadSimpleIcon, XIcon } from "@phosphor-icons/react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type PressableStateCallbackType,
} from "react-native";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomRichTextEditor from "../../../../../components/TextEditor/CustomRichTextEditor";
import useFileUpload, { type ProcessedFile } from "../../../../../hooks/useFileUpload";
import { colors } from "../../../../../styles/design-system";
import { StoragePathType } from "@axio/graphql/generated/graphql";
import type { ModernizerCreateActivityInput } from "./ModernizerAddActivityTypes";

interface ModernizerPageActivityFormProps {
  isSubmitting: boolean;
  onBack: () => void;
  onCancel: () => void;
  onSave: (input: ModernizerCreateActivityInput) => void;
}

interface UploadedPageFile {
  fileId: string;
  fileName: string;
}

function formatMaxFileSize(maxFileSize: number): string {
  if (!maxFileSize) return "500MB max";
  return `${Math.floor(maxFileSize / 1024 / 1024)}MB max`;
}

function ModernizerPageActivityForm({ isSubmitting, onBack, onCancel, onSave }: ModernizerPageActivityFormProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState<UploadedPageFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [activelyEditing, setActivelyEditing] = useState(false);

  const handleAfterUpload = (processedFilesArray: ProcessedFile[]) => {
    setUploadError(null);
    setFiles((currentFiles) => [
      ...currentFiles,
      ...processedFilesArray.map((processedFile) => ({
        fileId: processedFile.file.id,
        fileName: processedFile.file.fileName,
      })),
    ]);
    setIsUploading(false);
  };

  const { handleFileUpload, maxFileSize } = useFileUpload({
    storagePath: StoragePathType.Resources,
    beforeUpload: () => {
      setUploadError(null);
      setIsUploading(true);
    },
    afterUpload: handleAfterUpload,
    onError: (message) => {
      setIsUploading(false);
      setUploadError(message ?? "Failed to upload file. Please try again.");
    },
    multiple: true,
  });

  const handleContentChange = (nextContent: string) => {
    setActivelyEditing(true);
    setContent(nextContent);
  };

  const handleSubmit = () => {
    onSave({
      type: "page",
      input: {
        title: title.trim(),
        description: description.trim(),
        content,
        files: files.map((file) => ({ fileId: file.fileId })),
      },
    });
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles((currentFiles) => currentFiles.filter((file) => file.fileId !== fileId));
  };

  const handleUploadPress = async () => {
    const processedFiles = await handleFileUpload();
    if (!processedFiles?.length) {
      setIsUploading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Back to activity types"
            onPress={onBack}
            disabled={isSubmitting || isUploading}
            style={({ hovered }: PressableStateCallbackType) => [styles.iconButton, hovered && styles.iconButtonHover]}>
            <ArrowLeftIcon size={18} color={colors.grey[700]} />
          </Pressable>
          <CustomText text="Create Page" useTranslationText={false} size="l" weight="bold" style={styles.headerTitle} />
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close add activity"
          onPress={onCancel}
          disabled={isSubmitting || isUploading}
          style={({ hovered }: PressableStateCallbackType) => [styles.iconButton, hovered && styles.iconButtonHover]}>
          <XIcon size={18} color={colors.grey[500]} />
        </Pressable>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} tabIndex={0}>
        <CustomText text="Basics" useTranslationText={false} size="m" weight="medium" style={styles.sectionLabel} />
        <CustomSpacing type="vertical" size="s" />

        <CustomText text="Page Title" useTranslationText={false} size="s" weight="medium" style={styles.fieldLabel} />
        <CustomSpacing type="vertical" size="xs" />
        <CustomTextInput placeholder="Enter page title" value={title} onChangeText={setTitle} />

        <CustomSpacing type="vertical" size="m" />
        <CustomText
          text="Short Description"
          useTranslationText={false}
          size="s"
          weight="medium"
          style={styles.fieldLabel}
        />
        <CustomSpacing type="vertical" size="xs" />
        <CustomTextInput
          placeholder="Optional one-liner shown in module navigation"
          value={description}
          onChangeText={setDescription}
        />

        <View style={styles.divider} />

        <CustomText
          text="Page Content"
          useTranslationText={false}
          size="m"
          weight="ultraBold"
          style={styles.sectionLabel}
        />
        <CustomSpacing type="vertical" size="s" />
        <CustomRichTextEditor
          value={content}
          onChange={handleContentChange}
          isSubmitting={isSubmitting}
          placeholder="Write the page content students will read..."
          activelyEditing={activelyEditing}
          restrictImageUpload={false}
        />

        <View style={styles.divider} />

        <CustomText
          text="Attachments"
          useTranslationText={false}
          size="m"
          weight="ultraBold"
          style={styles.sectionLabel}
        />
        <CustomSpacing type="vertical" size="s" />
        <CustomText text="Files" useTranslationText={false} size="s" weight="medium" style={styles.fieldLabel} />
        <CustomSpacing type="vertical" size="xs" />
        <CustomText
          text="Optional supporting files for this page"
          useTranslationText={false}
          size="xs"
          style={styles.helperText}
        />
        <CustomSpacing type="vertical" size="s" />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Upload page files"
          onPress={handleUploadPress}
          disabled={isSubmitting || isUploading}
          style={({ hovered }: PressableStateCallbackType) => [styles.uploadBox, hovered && styles.uploadBoxHover]}>
          {isUploading ? (
            <ActivityIndicator size="small" color={colors.brand[600]} />
          ) : (
            <UploadSimpleIcon size={30} color={colors.brand[600]} />
          )}
          <CustomText
            text={isUploading ? "Uploading..." : "Drag & drop or browse"}
            useTranslationText={false}
            size="s"
            weight="medium"
            style={styles.uploadText}
          />
          <CustomText
            text={formatMaxFileSize(maxFileSize)}
            useTranslationText={false}
            size="xs"
            style={styles.uploadLimit}
          />
        </Pressable>
        {uploadError ? (
          <>
            <CustomSpacing type="vertical" size="s" />
            <CustomText text={uploadError} useTranslationText={false} size="xs" style={styles.errorText} />
          </>
        ) : null}
        {files.length > 0 ? (
          <View style={styles.fileList}>
            {files.map((file) => (
              <View key={file.fileId} style={styles.fileRow}>
                <View style={styles.fileInfo}>
                  <FileTextIcon size={16} color={colors.grey[600]} />
                  <CustomText text={file.fileName} useTranslationText={false} size="s" style={styles.fileName} />
                </View>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Remove ${file.fileName}`}
                  onPress={() => handleRemoveFile(file.fileId)}
                  disabled={isSubmitting}
                  style={({ hovered }: PressableStateCallbackType) => [
                    styles.removeButton,
                    hovered && styles.removeButtonHover,
                  ]}>
                  <TrashIcon size={14} color={colors.grey[500]} />
                </Pressable>
              </View>
            ))}
          </View>
        ) : null}
      </ScrollView>

      <View style={styles.footer}>
        <CustomButton
          text="Cancel"
          size="s"
          styleType="primaryTransparentStroke"
          onPress={onCancel}
          disabled={isSubmitting || isUploading}
          style={styles.cancelButton}
        />
        <CustomButton
          text="Create Page"
          size="s"
          styleType="primary"
          onPress={handleSubmit}
          disabled={!title.trim() || isSubmitting || isUploading}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: "100%",
    backgroundColor: colors.white,
  },
  header: {
    minHeight: 64,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: colors.white,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  headerTitle: {
    color: colors.grey[900],
  },
  iconButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  iconButtonHover: {
    backgroundColor: colors.grey[100],
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 32,
    paddingTop: 32,
    paddingBottom: 40,
  },
  sectionLabel: {
    color: colors.grey[600],
    textTransform: "uppercase",
  },
  fieldLabel: {
    color: colors.grey[800],
  },
  helperText: {
    color: colors.grey[600],
  },
  divider: {
    height: 1,
    backgroundColor: colors.grey[200],
    marginVertical: 24,
  },
  uploadBox: {
    minHeight: 126,
    borderWidth: 1.5,
    borderStyle: "dashed",
    borderColor: colors.brand[300],
    borderRadius: 16,
    backgroundColor: colors.white,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  uploadBoxHover: {
    backgroundColor: colors.brand[50],
    borderColor: colors.brand[500],
  },
  uploadText: {
    marginTop: 10,
    color: colors.grey[800],
  },
  uploadLimit: {
    marginTop: 4,
    color: colors.brand[600],
  },
  errorText: {
    color: colors.error[600],
  },
  fileList: {
    marginTop: 12,
    gap: 8,
  },
  fileRow: {
    minHeight: 44,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 8,
    backgroundColor: colors.grey[50],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  fileInfo: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  fileName: {
    color: colors.grey[800],
    flex: 1,
  },
  removeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
  },
  removeButtonHover: {
    backgroundColor: colors.grey[200],
  },
  footer: {
    minHeight: 72,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    backgroundColor: colors.white,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cancelButton: {
    backgroundColor: colors.white,
    borderWidth: 0,
  },
});

export default ModernizerPageActivityForm;
